import { Link, useNavigate } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatINR } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";

export default function ProductCard({ product }) {
  const { toggleWishlist, isWishlisted, addToCart } = useStore();
  const navigate = useNavigate();
  const p = product;
  const wl = isWishlisted(p.id);
  const out = !p.inStock;

  const quickAdd = (e) => {
    e.preventDefault();
    if (p.hasVariants) { navigate(`/product/${p.slug}`); return; }
    const v = (p.variants || [])[0];
    if (!v) return;
    addToCart({ productId: p.id, variantId: v.id, name: p.name, slug: p.slug, image: p.images?.[0], price: v.price, mrp: v.mrp, attributes: v.attributes, quantity: 1, stock: v.stock });
  };

  return (
    <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`}
      className="group relative bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden">
      {p.discountPercent > 0 && (
        <span className="absolute top-3 left-3 z-10 bg-amber-500 text-white text-[11px] font-bold px-2 py-1 rounded-md shadow-sm">{p.discountPercent}% OFF</span>
      )}
      <button
        onClick={(e) => { e.preventDefault(); toggleWishlist(p.id); }}
        data-testid={`wishlist-btn-${p.slug}`}
        aria-label="Wishlist"
        className={`absolute top-3 right-3 z-10 p-2 rounded-full bg-white/90 backdrop-blur-sm shadow transition ${wl ? "text-red-500" : "text-slate-500 hover:text-red-500"}`}>
        <Heart className="h-4 w-4" fill={wl ? "currentColor" : "none"} />
      </button>

      <div className="aspect-square w-full overflow-hidden bg-slate-50 p-4 flex items-center justify-center">
        <img src={resolveMediaUrl(p.images?.[0])} alt={p.name} loading="lazy"
          onError={handleMediaError}
          className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-500" />
      </div>

      <div className="p-3 sm:p-4 flex flex-col flex-1">
        {p.brand?.name && <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{p.brand.name}</span>}
        <h3 className="text-sm font-semibold text-slate-900 line-clamp-2 mt-0.5 min-h-[2.5rem]">{p.name}</h3>
        {p.reviewCount > 0 && (
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
            <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {p.rating} ({p.reviewCount})
          </div>
        )}
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-lg font-bold text-slate-900 font-mono">{formatINR(p.price)}</span>
          {p.mrp > p.price && <span className="text-xs text-slate-400 line-through font-mono">{formatINR(p.mrp)}</span>}
        </div>
        <div className="mt-auto pt-3">
          {out ? (
            <span className="block text-center text-xs font-semibold text-slate-400 border border-slate-200 rounded-lg py-2">Out of Stock</span>
          ) : (
            <button onClick={quickAdd} data-testid={`add-to-cart-${p.slug}`}
              className="w-full text-sm font-semibold text-white bg-slate-900 hover:bg-amber-500 rounded-lg py-2 transition-colors">
              {p.hasVariants ? "Select Options" : "Add to Cart"}
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
      <div className="aspect-square bg-slate-100" />
      <div className="p-4 space-y-2">
        <div className="h-3 w-1/3 bg-slate-100 rounded" />
        <div className="h-4 w-full bg-slate-100 rounded" />
        <div className="h-5 w-1/2 bg-slate-100 rounded" />
        <div className="h-9 w-full bg-slate-100 rounded-lg mt-3" />
      </div>
    </div>
  );
}
